import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';

export default function DataForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/data-requests/public/form/${token}`)
      .then(res => { setData(res.data); setAnswer(res.data.currentAnswer || ''); setLoading(false); })
      .catch(() => { setError('Form not found or expired'); setLoading(false); });
  }, [token]);

  const submit = async () => {
    if (!answer.trim()) return;
    try {
      await api.post(`/data-requests/public/form/${token}`, { answer: answer.trim() });
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ fontSize: 16, color: '#64748b' }}>Loading...</div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>:(</div>
        <div style={{ fontSize: 16, color: '#64748b' }}>{error}</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0fdf4' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: 500 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>Thank You!</div>
        <div style={{ fontSize: 18, color: '#16a34a', fontWeight: 600, marginBottom: 8 }}>Your answer has been saved</div>
        <div style={{ fontSize: 14, color: '#64748b' }}>Order {data.reference} — ${answer}</div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 16 }}>You can close this page now.</div>
      </div>
    </div>
  );

  const od = data.orderDetails || {};
  const items = od.items || [];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 20px' }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ background: '#1565c0', color: '#fff', padding: '24px 28px', borderRadius: '16px 16px 0 0' }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Shager Tracker</h1>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>Data Request</div>
        </div>

        <div style={{ background: '#fff', padding: 28, borderRadius: '0 0 16px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {/* Order Info */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 12px', fontSize: 14 }}>
              <span style={{ color: '#64748b' }}>Order Number:</span>
              <span style={{ fontWeight: 700 }}>{data.reference}</span>
              {od.origin && <><span style={{ color: '#64748b' }}>Origin:</span><span>{od.origin}</span></>}
              {od.transport && <><span style={{ color: '#64748b' }}>Transport:</span><span>{od.transport}</span></>}
              {od.arrivalDate && <><span style={{ color: '#64748b' }}>Arrival Date:</span><span>{new Date(od.arrivalDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></>}
              {od.totalAmount > 0 && <><span style={{ color: '#64748b' }}>Total Amount:</span><span style={{ fontWeight: 700, color: '#1565c0' }}>${od.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></>}
            </div>
          </div>

          {/* Items table */}
          {items.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Order Items</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'left' }}>SKU</th>
                    <th style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'left' }}>Product</th>
                    <th style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Unit Price</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i}>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{item.sku}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{item.description}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 600 }}>{parseFloat(item.quantity).toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Question + Input */}
          <div style={{ background: '#fffbeb', border: '2px solid #fde68a', borderRadius: 10, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#92400e', marginBottom: 12 }}>{data.question}</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#64748b' }}>$</span>
              <input
                type="number" step="0.01" autoFocus
                value={answer} onChange={e => setAnswer(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder="Enter amount in USD"
                style={{ flex: 1, padding: '12px 16px', fontSize: 18, border: '2px solid #e2e8f0', borderRadius: 8, outline: 'none', fontFamily: "'DM Mono', monospace" }}
              />
            </div>
          </div>

          <button onClick={submit} disabled={!answer.trim()}
            style={{ width: '100%', padding: '14px', fontSize: 16, fontWeight: 700, background: answer.trim() ? '#1565c0' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 10, cursor: answer.trim() ? 'pointer' : 'default' }}>
            Submit Answer
          </button>

          {data.answered && (
            <div style={{ marginTop: 12, fontSize: 13, color: '#16a34a', textAlign: 'center' }}>
              Previously answered: ${data.currentAnswer}. You can update it above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
